import { offlineDb } from './offline-db'

const SESSION_ID = 'default'

/**
 * @typedef {Object} LocalSessionRecord
 * @property {string} id
 * @property {string} [refreshToken]
 * @property {number} updatedAt
 */

/**
 * @returns {Promise<LocalSessionRecord | undefined>}
 */
export async function getLocalSession() {
  return offlineDb.localSessions.get(SESSION_ID)
}

/**
 * @param {string} [refreshToken]
 */
export async function saveRefreshToken(refreshToken) {
  const prev = (await getLocalSession()) || { id: SESSION_ID, updatedAt: Date.now() }
  await offlineDb.localSessions.put({
    ...prev,
    id: SESSION_ID,
    refreshToken: refreshToken || prev.refreshToken,
    updatedAt: Date.now()
  })
}

export async function clearLocalSessionSecrets() {
  await offlineDb.localSessions.delete(SESSION_ID)
}

/**
 * Read access token from localStorage (single source for Bearer).
 * @returns {string | null}
 */
export function getAccessTokenFromStorage() {
  try {
    const raw = localStorage.getItem('adams_user')
    if (!raw) return null
    const u = JSON.parse(raw)
    return u?.token || localStorage.getItem('token') || null
  } catch {
    return null
  }
}

/**
 * Auth/Init payload persisted in localStorage (fallback when route state is missing).
 * @returns {object}
 */
export function getInitFromStorage() {
  try {
    const raw = localStorage.getItem('adams_user')
    const u = raw ? JSON.parse(raw) : null
    return u?.init && typeof u.init === 'object' ? u.init : {}
  } catch {
    return {}
  }
}

/**
 * @param {string} token
 * @param {string} [refreshToken]
 */
export function updateTokensInLocalStorage(token, refreshToken) {
  try {
    const raw = localStorage.getItem('adams_user')
    const u = raw ? JSON.parse(raw) : {}
    u.token = token
    if (refreshToken) u.refreshToken = refreshToken
    localStorage.setItem('adams_user', JSON.stringify(u))
    localStorage.setItem('token', token)
  } catch {
    localStorage.setItem('token', token)
  }
}

/**
 * Persist Auth/Init payload after structure or session updates (Admin, Home org switch).
 * @param {object} init
 * @param {string} [token]
 */
export function persistAdamsUserInit(init, token) {
  try {
    const raw = localStorage.getItem('adams_user')
    const u = raw ? JSON.parse(raw) : {}
    if (token) u.token = token
    u.init = init
    localStorage.setItem('adams_user', JSON.stringify(u))
  } catch {
    /* ignore quota / private mode */
  }
}
